/**
 * Recent projects - the projects THIS device opened, most recent first.
 *
 * Per device and never synced, on purpose: "recent" is a fact about the
 * screen in your hand (the iPad on the stand opens the gig set, the laptop
 * opens the rehearsal project). A synced list would show the laptop's
 * rehearsal at the top of the iPad's Navigate mid-gig.
 *
 * Nothing recorded this before (projects carry `createdAt` / `modifiedAt` for
 * sync merging only), so Navigate falls back to `modifiedAt` for projects this
 * device has never switched to.
 *
 * Read by the Project section, which lists projects newest first. Recorded
 * from `switchProject()`: BOTH the project being left and the one
 * being opened are stamped. Stamping only the opened one would never record
 * the project the app booted into until something else was opened first.
 */

import { createSignal, type Accessor } from 'solid-js';

const KEY = 'opbuttons.recentProjects.v1';
const MAX = 12;

export interface RecentEntry { id: string; at: number }

function load(): RecentEntry[] {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is RecentEntry =>
      !!e && typeof (e as RecentEntry).id === 'string' && typeof (e as RecentEntry).at === 'number');
  } catch {
    return [];
  }
}

function save(list: RecentEntry[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* private mode / quota: recents are a convenience */ }
}

const [list, setList] = createSignal<RecentEntry[]>(load());

export const recentProjectEntries: Accessor<RecentEntry[]> = list;

export function noteProjectOpened(id: string | null, at: number = Date.now()): void {
  if (!id) return;
  const next = [{ id, at }, ...list().filter((e) => e.id !== id)].slice(0, MAX);
  setList(next);
  save(next);
}
